// Global unsaved-changes guard.
//
// Screens with buffered/unsaved state register a check here; every exit
// point that would silently lose that state asks the registry first:
//   - the sidebar navigation (LeftSidebar.handleNavigation, SidebarItem)
//   - the webshop picker (LeftSidebarMenu)
//   - the browser itself (beforeunload → native "leave site?" dialog)
//
// A check returns a LIST OF LABELS describing what's unsaved (e.g.
// ["Opmaak · Menubalk", "Data · Landen (3 rijen)"]) — or an empty array /
// false when clean. The labels feed the confirmation dialog so the user
// sees exactly what would be lost.
//
// Tab switches INSIDE a screen don't go through this — those keep their
// state and need no warning.
//
// Usage in a screen:
//   const itemsRef = useRef([]);
//   itemsRef.current = myDirtyLabels;          // updated every render
//   useEffect(() => {
//       registerUnsavedCheck('my-screen', () => itemsRef.current);
//       return () => unregisterUnsavedCheck('my-screen');
//   }, []);

import i18n from '../i18n';

const checks = new Map();
const savers = new Map();
let beforeUnloadInstalled = false;
// Set by the UnsavedChangesDialog host (mounted once in App). Receives the
// item list + the savers and resolves to true (leave) / false (stay).
let dialogOpener = null;

const ensureBeforeUnload = () => {
    if (beforeUnloadInstalled || typeof window === 'undefined') return;
    beforeUnloadInstalled = true;
    window.addEventListener('beforeunload', (e) => {
        if (hasUnsavedChanges()) {
            // Browsers only allow their native dialog here — no custom UI.
            e.preventDefault();
            e.returnValue = '';
        }
    });
};

// `save` (optional) lets the dialog offer "Save & continue": an async fn
// that persists the screen's pending changes. Should resolve when done.
export const registerUnsavedCheck = (id, fn, save) => {
    checks.set(id, fn);
    if (typeof save === 'function') savers.set(id, save);
    ensureBeforeUnload();
};

export const unregisterUnsavedCheck = (id) => {
    checks.delete(id);
    savers.delete(id);
};

/** Run every registered screen's save fn (dirty or not — a clean save no-ops). */
export const runUnsavedSaves = async () => {
    await Promise.all(Array.from(savers.values()).map(async (fn) => {
        try { await fn(); } catch { /* the screen surfaces its own error */ }
    }));
};

export const hasAnySaver = () => savers.size > 0;

export const setUnsavedDialogOpener = (fn) => {
    dialogOpener = fn;
};

/** All unsaved-item labels across the registered screens. */
export const collectUnsavedItems = () => {
    const items = [];
    for (const fn of checks.values()) {
        try {
            const r = fn();
            if (Array.isArray(r)) items.push(...r.filter(Boolean));
            else if (r) items.push(typeof r === 'string' ? r : '');
        } catch { /* a broken check never blocks navigation */ }
    }
    return items.filter(Boolean);
};

export const hasUnsavedChanges = () => collectUnsavedItems().length > 0 || anyTruthyCheck();

// Backstop for checks that return a bare `true` instead of labels.
const anyTruthyCheck = () => {
    for (const fn of checks.values()) {
        try {
            const r = fn();
            if (r === true) return true;
        } catch { /* ignore */ }
    }
    return false;
};

/**
 * Ask the user whether it's OK to leave. Resolves true when it's safe:
 * either nothing is unsaved, or the user explicitly chose to discard.
 * Shows the rich dialog (with the what-would-be-lost list) when the host
 * is mounted; falls back to window.confirm otherwise.
 */
export const requestLeaveConfirmation = async () => {
    const items = collectUnsavedItems();
    if (items.length === 0 && !anyTruthyCheck()) return true;
    if (typeof dialogOpener === 'function') {
        return dialogOpener(items);
    }
    // eslint-disable-next-line no-alert
    return window.confirm(i18n.t('confirm_unsaved_changes', 'Je hebt onbewaarde wijzigingen. Doorgaan zonder op te slaan?'));
};

// Exposed so the dialog can re-evaluate after a save (the registered checks
// read live refs, so this is accurate on the tick after the save's setState).
export { collectUnsavedItems as currentUnsavedItems };

/**
 * Synchronous legacy variant (window.confirm) — kept for call sites that
 * can't await. Prefer requestLeaveConfirmation().
 */
export const confirmLeaveUnsaved = (message) => {
    if (!hasUnsavedChanges()) return true;
    const msg = message || i18n.t('confirm_unsaved_changes', 'Je hebt onbewaarde wijzigingen. Doorgaan zonder op te slaan?');
    // eslint-disable-next-line no-alert
    return window.confirm(msg);
};
